// src/utils/profileCompletion.js

/**
 * Calcule le pourcentage de complétion du profil patient
 * @param {Object} params
 * @param {Object} params.user - Données utilisateur (firstName, lastName, email, phone, address, avatarSrc, etc.)
 * @param {Object} params.metrics - Données médicales (groupeSanguin, maladiesChroniques, insurances, etc.)
 * @param {number} params.height - Taille en cm
 * @param {number} params.weight - Poids en kg
 * @param {Object} params.history - Historique médical { lifestyle, allergies, surgeries, familyHistory, chronic }
 * @param {Object} params.options - Options de calcul
 * @returns {Object} { percent, missing, filled, total }
 */
export function computeProfileCompletion({
  user,
  metrics,
  height,
  weight,
  history,
  options = {},
}) {
  const {
    includeHistory = true,
    requireAnyHistoryItem = false,
    medicalLoading = false,
    basicLoading = false,
    historyLoading = false,
  } = options;

  // Si les données chargent encore, on retourne 0
  if (medicalLoading || basicLoading || historyLoading) {
    return { percent: 0, missing: [], filled: [], total: 0 };
  }

  const fields = [];

  // ===== SECTION 1: Informations de base (user) =====
  fields.push({
    key: "firstName",
    label: "First Name",
    value: user?.firstName,
    required: true,
  });

  fields.push({
    key: "lastName",
    label: "Last Name",
    value: user?.lastName,
    required: true,
  });

  fields.push({
    key: "email",
    label: "Email",
    value: user?.email,
    required: true,
  });

  fields.push({
    key: "phone",
    label: "Phone",
    value: user?.phone,
    required: true,
  });

  fields.push({
    key: "address",
    label: "Address",
    value: user?.address,
    required: true,
  });

  fields.push({
    key: "avatar",
    label: "Profile Picture",
    value: user?.avatarSrc || user?.avatar || user?.photo,
    required: true,
  });

  fields.push({
    key: "gender",
    label: "Gender",
    value: user?.gender,
    required: true,
  });

  // ===== SECTION 2: Informations médicales (metrics) =====
  fields.push({
    key: "bloodType",
    label: "Blood Type",
    value: metrics?.groupeSanguin,
    required: true,
  });

  fields.push({
    key: "height",
    label: "Height",
    value: height,
    required: true,
  });

  fields.push({
    key: "weight",
    label: "Weight",
    value: weight,
    required: true,
  });

  // Maladies chroniques (optionnel mais compté)
  fields.push({
    key: "chronicDiseases",
    label: "Chronic Diseases",
    value: metrics?.maladiesChroniques,
    required: false,
  });

  // Assurances (optionnel mais compté)
  const hasInsurance =
    Array.isArray(metrics?.insurances) && metrics.insurances.length > 0;
  fields.push({
    key: "insurance",
    label: "Insurance",
    value: hasInsurance ? "yes" : null,
    required: false,
  });

  // ===== SECTION 3: Historique médical (optionnel) =====
  if (includeHistory && history) {
    const { lifestyle, allergies, surgeries, familyHistory, chronic } = history;

    // On compte si au moins un item existe dans l'historique
    const hasAnyHistory =
      (Array.isArray(lifestyle) && lifestyle.length > 0) ||
      (Array.isArray(allergies) && allergies.length > 0) ||
      (Array.isArray(surgeries) && surgeries.length > 0) ||
      (Array.isArray(familyHistory) && familyHistory.length > 0) ||
      (Array.isArray(chronic) && chronic.length > 0);

    fields.push({
      key: "history",
      label: "Medical History",
      value: hasAnyHistory ? "yes" : null,
      required: requireAnyHistoryItem, // ✅ false par défaut = history n'empêche pas 100%
    });
  }

  // ===== CALCUL =====
  const filled = [];
  const missing = [];

  for (const field of fields) {
    const hasValue =
      field.value !== null &&
      field.value !== undefined &&
      field.value !== "" &&
      String(field.value).trim().length > 0;

    if (hasValue) {
      filled.push(field.key);
    } else if (field.required) {
      missing.push(field.label);
    }
  }

  const requiredCount = fields.filter((f) => f.required).length;
  const percent =
    requiredCount > 0 ? Math.round((filled.length / requiredCount) * 100) : 100;

  return {
    percent: Math.min(percent, 100),
    missing,
    filled,
    total: requiredCount,
  };
}

/**
 * Event personnalisé pour notifier les changements de profil
 */
export function triggerProfileUpdate() {
  if (typeof window !== "undefined") {
    const event = new CustomEvent("profileUpdated", { detail: { timestamp: Date.now() } });
    window.dispatchEvent(event);
  }
}